import React, { useRef, useEffect, useCallback, useState } from "react";
import { centreOnPerson } from "./centreOnPerson";
import * as f3 from "family-chart";
import "family-chart/styles/family-chart.css";
import "./FamilyTree.css";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authentication";
import { SharedButton } from "../../shared";
import {
  useGetFamilyTreeQuery,
  useGetAdminStatusQuery,
  useAdminUpdateIndividualMutation,
  useAdminCreateIndividualMutation,
  useAdminDeleteIndividualMutation,
} from "../../../store/slices/api";
import FamilyTreeLoading from "./FamilyTreeLoading";
import TreeLegend from "./TreeLegend";
import LineageView from "./LineageView";
import { TREE_ROOT_ID } from "./treeRoot";
import { normalizeName, squashName, nameMatches } from "../../shared/nameSearch";

// Build the display name. When a person has a married name, show the maiden
// name in the middle: "First (Maiden) MarriedName Suffix". Falls back to
// the plain "First Maiden Suffix" when there is no married name, so nothing
// changes for the vast majority of the tree.
function formatName(d) {
  if (!d) return "";
  const first = d.first_name || "";
  const maiden = d.last_name || "";
  const married = d.married_name || "";
  const suffix = d.numeric_suffix || "";
  let core;
  if (married && maiden && married.trim() !== maiden.trim()) {
    core = `${first} (${maiden}) ${married}`;
  } else if (married && !maiden) {
    core = `${first} ${married}`;
  } else {
    core = `${first} ${maiden}`;
  }
  return `${core} ${suffix}`.replace(/\s+/g, " ").trim();
}

// Split the name into separate card lines so the married name gets its OWN
// line (no ugly "du / Pont" wrap):
//   line 1: first names        e.g. "Francine Joan"
//   line 2: "(Maiden)"        only when there's a married name, else blank
//   line 3: current surname    the married name if set, otherwise the maiden
function nameLines(d) {
  if (!d) return { name_l1: "", name_l2: "", name_l3: "" };
  const first = d.first_name || "";
  const maiden = d.last_name || "";
  const married = d.married_name || "";
  const suffix = d.numeric_suffix || "";
  const showMaiden = married && maiden && married.trim() !== maiden.trim();
  const current = married || maiden;
  return {
    name_l1: first,
    name_l2: showMaiden ? `(${maiden})` : "",
    name_l3: `${current} ${suffix}`.replace(/\s+/g, " ").trim(),
  };
}

function TreeView() {
  const chartRef = useRef(null);
  const f3ChartRef = useRef(null);
  const f3CardRef = useRef(null);
  const originalTreeRef = useRef(null);
  const [isResettingTree, setIsResettingTree] = useState(false);
  const [showCurtain, setShowCurtain] = useState(true);
  // Cancels a pending "wait until the card can be centred" loop. Cancelled
  // before starting another and on unmount, so two loops can never run at once.
  const cancelCentreRef = useRef(null);
  // Whether we have actually managed to centre yet. The tab can be mounted
  // while hidden, in which case the first attempt cannot succeed.
  const hasCentredRef = useRef(false);
  const [chartReady, setChartReady] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [adminModeActive, setAdminModeActive] = useState(false);
  const [lineageTarget, setLineageTarget] = useState(null);
  const [lineageHistory, setLineageHistory] = useState([]);
  // The search the current results belong to (empty when there is none), so
  // "No matches" can name it.
  const [searchedFor, setSearchedFor] = useState("");
  const searchTimeoutRef = useRef(null);
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;
  const {
    data: familyTree,
    isLoading: isLoadingFamilyTree,
    isFetching: isFetchingFamilyTree,
    error: familyTreeError,
    refetch: refetchFamilyTree,
  } = useGetFamilyTreeQuery();
  const { data: adminData } = useGetAdminStatusQuery(individualId, {
    skip: !individualId,
  });
  const isAdmin = adminData?.is_admin || false;
  const [adminUpdateIndividual] = useAdminUpdateIndividualMutation();
  const [adminCreateIndividual] = useAdminCreateIndividualMutation();
  const [adminDeleteIndividual] = useAdminDeleteIndividualMutation();

  // Initialize chart — only builds once, skips if already built
  useEffect(() => {
    if (
      chartRef.current &&
      familyTree &&
      !isLoadingFamilyTree &&
      user?.externalId &&
      !f3ChartRef.current // Skip if chart already exists (e.g. returning from another tab)
    ) {
      // Make sure the loading screen is visible
      setChartReady(false);
      setShowCurtain(true);

      // Use requestAnimationFrame to let the browser paint the loading screen
      // BEFORE we start the heavy chart work (which blocks the browser)
      const rafId = requestAnimationFrame(() => {
        setTimeout(() => {
          // Keep a deep copy of the original tree
          originalTreeRef.current = structuredClone(familyTree);

          // Pre-compute each card's name lines so the married name lands on its
          // own line (raw fields first_name / last_name / married_name stay
          // clean for admin editing).
          originalTreeRef.current.forEach((node) => {
            if (!node?.data) return;

            // family-chart sorts every parent's children by the order of that
            // parent's spouses, and flips the direction based on `data.gender`
            // (sortChildrenWithSpouses, family-chart.js:27-38). Our payload has
            // `sex`, not `gender` (family_tree/get.py:200), so `gender` was
            // undefined for all 7,609 people and the `=== "M"` test never
            // passed. Every father's children were laid out in REVERSE spouse
            // order while his spouse cards stayed in forward order, so the
            // connector lines crossed each other. ConnectionPathFinder.jsx:44
            // already applies this same shim; TreeView never did.
            if (node.data.sex && !node.data.gender) {
              node.data.gender = node.data.sex;
            }

            Object.assign(node.data, nameLines(node.data));
          });

          const f3Chart = f3.createChart(
            "#FamilyChart",
            originalTreeRef.current
          );
          // Give the connector lines room so they don't crowd, and fit the
          // taller (née) cards. Library defaults are x=250, y=150.
          f3Chart.setCardXSpacing(290).setCardYSpacing(210);
          f3ChartRef.current = f3Chart;

          const f3Card = f3Chart
            .setCardHtml()
            .setCardDisplay([
              ["name_l1"],
              ["name_l2"],
              ["name_l3"],
              ["birthday"],
              ["spouse_label"],
            ]);
          f3CardRef.current = f3Card;

          // Lineage mode — click a card to open their ancestry mini-tree
          // (admin mode uses its own click handler, so we skip when active)
          f3Card.setOnCardClick((e, datum) => {
            if (adminModeActive) return; // let admin edit form handle it
            const personId = datum.data.id;
            const personData = familyTree.find((n) => String(n.id) === String(personId));
            const name = personData
              ? `${personData.data.first_name || ""} ${personData.data.last_name || ""} ${personData.data.numeric_suffix || ""}`.trim()
              : "Unknown";
            setLineageTarget(personId);
            setLineageHistory([{ id: personId, name }]);
          });

          // Name the root BEFORE drawing (see treeRoot.js). The member is
          // centred below without changing the root.
          f3Chart.updateMainId(TREE_ROOT_ID);
          f3Chart.updateTree({ initial: true });

          // Open on YOU. The founder is the ROOT of the layout (frontend #47);
          // where the VIEW starts is a separate thing, and it should start on
          // the person looking at it.
          //
          // This used to measure and centre immediately. That is a race, and
          // with the whole family on screen the browser regularly lost it: the
          // layout had not finished, the card had no coordinates yet, and the
          // tree opened scrolled into empty space with a few cards clipped at
          // the top (Sam, on the live site, 2026-09-17). It is intermittent,
          // which is why it survived testing -- a faster load centres fine.
          if (cancelCentreRef.current) cancelCentreRef.current();
          cancelCentreRef.current = centreOnPerson({
            getChart: () => f3ChartRef.current,
            personId: user.externalId,
            f3,
            onDone: (ok) => { hasCentredRef.current = ok; },
          });

          setChartReady(true);
        }, 50); // Small delay ensures the loading screen actually paints
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (cancelCentreRef.current) cancelCentreRef.current();
      };
    }
    // adminModeActive intentionally omitted — the admin edit tree overrides card
    // clicks via setCardClickOpen, so we don't need to re-run this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyTree, isLoadingFamilyTree, user?.externalId]);

  // The Family Tree tab stays MOUNTED while another tab is showing, inside a
  // `hidden` div (FamilyTree/index.jsx:82). So arriving on `?tab=cousinLookup`,
  // or reloading while another tab is open, builds the whole chart at ZERO
  // SIZE — and nothing can be centred in a box with no dimensions. That is not
  // a race; it happens every time. An audit of this very fix found it
  // (2026-09-17), and the first version of the fix gave up silently on it.
  //
  // So: watch the container. The moment it is given a size, if we have not
  // managed to centre yet, centre now.
  useEffect(() => {
    const host = chartRef.current;
    if (!host || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver((entries) => {
      const box = entries[0] && entries[0].contentRect;
      if (!box || box.width < 1 || box.height < 1) return;
      if (hasCentredRef.current || !f3ChartRef.current || !user?.externalId) return;

      if (cancelCentreRef.current) cancelCentreRef.current();
      cancelCentreRef.current = centreOnPerson({
        getChart: () => f3ChartRef.current,
        personId: user.externalId,
        f3,
        onDone: (ok) => { hasCentredRef.current = ok; },
      });
    });

    observer.observe(host);
    return () => observer.disconnect();
  }, [user?.externalId]);

  // Reset tree — full reset to original view:
  // closes lineage mode, resets zoom, and re-centers on you
  const handleResetFullTree = useCallback(() => {
    if (!f3ChartRef.current || !user?.externalId) return;

    // Close lineage view if open
    setLineageTarget(null);
    setLineageHistory([]);

    // Reset zoom back to default
    f3.handlers.zoomTo(f3ChartRef.current.svg, 1);

    // Also reset the zoom slider if it exists
    const slider = document.querySelector(".zoom-slider");
    if (slider) slider.value = "1";

    // Only show loading screen if the reset takes longer than 0.9s
    const loadingTimer = setTimeout(() => {
      setChartReady(false);
      setShowCurtain(true);
    }, 900);

    requestAnimationFrame(() => {
      setTimeout(() => {
        // The same single centring path the first load uses. Reset was already
        // reliable because the chart is laid out by then, but two copies of
        // this logic is how they drift apart.
        if (cancelCentreRef.current) cancelCentreRef.current();
        cancelCentreRef.current = centreOnPerson({
          getChart: () => f3ChartRef.current,
          personId: user.externalId,
          f3,
          onDone: (ok) => { hasCentredRef.current = ok; },
        });

        // Cancel loading screen if we finished fast enough
        clearTimeout(loadingTimer);
        setChartReady(true);
        setShowCurtain(false);
        setIsResettingTree(false);
      }, 50);
    });
  }, [user?.externalId]);

  // Pre-compute normalized names once (not on every keystroke)
  const normalizedPeople = React.useMemo(() => {
    if (!familyTree) return [];
    return familyTree.map((person) => {
      // searchable_married_name is the name a woman married into. It falls
      // back to her husband's surname when nobody has typed one in, so she is
      // findable under it either way. Search only — never shown on the card.
      const searchText = `${person.data.first_name || ""} ${
        person.data.last_name || ""
      } ${person.data.married_name || ""} ${
        person.data.searchable_married_name || ""
      } ${person.data.numeric_suffix || ""}`;
      return {
        person,
        normalized: normalizeName(searchText),
        squashed: squashName(searchText),
      };
    });
  }, [familyTree]);

  // Search — ignores accents, apostrophe style and spaces; multi-word, any order
  // e.g. "du pont pierre" finds "Pierre Samuel du Pont de Nemours"
  const handleSearchChange = useCallback(
    (value) => {
      setSearchQuery(value);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!value.trim()) {
        setSearchResults([]);
        setSearchedFor("");
        setShowSearchDropdown(false);
        return;
      }

      searchTimeoutRef.current = setTimeout(() => {
        const matches = [];
        for (let i = 0; i < normalizedPeople.length && matches.length < 50; i++) {
          const { person, normalized, squashed } = normalizedPeople[i];
          if (nameMatches(value, normalized, squashed)) {
            matches.push(person);
          }
        }
        setSearchResults(matches);
        setSearchedFor(value.trim());
        // Open even with no matches, to say so rather than show nothing.
        setShowSearchDropdown(true);
      }, 200);
    },
    [normalizedPeople]
  );

  // When a search result is selected, jump to that person in the tree. Search
  // covers everyone in the records, but not everyone is on the drawn chart;
  // for anyone who isn't, open their ancestry view, as clicking a card does,
  // instead of doing nothing.
  const handleSearchSelect = useCallback(
    (person) => {
      const tree = f3ChartRef.current?.store.getTree();
      const node = tree?.data.find(
        (n) => n.data.id === String(person.id)
      );
      if (node) {
        f3.handlers.cardToMiddle({
          datum: node,
          svg: f3ChartRef.current.svg,
          svg_dim: f3ChartRef.current.svg.getBoundingClientRect(),
          transition_time: 1000,
        });
      } else if (!adminModeActive) {
        const personId = String(person.id);
        const name = `${person.data.first_name || ""} ${person.data.last_name || ""} ${person.data.numeric_suffix || ""}`.trim();
        setLineageTarget(personId);
        setLineageHistory([{ id: personId, name: name || "Unknown" }]);
      }

      setSearchQuery("");
      setSearchResults([]);
      setSearchedFor("");
      setShowSearchDropdown(false);
    },
    [adminModeActive]
  );

  // Admin mode — toggle on/off with a warning before entering
  const handleAdminModeToggle = useCallback(() => {
    if (adminModeActive) {
      // Turning off admin mode — destroy the edit tree
      if (f3ChartRef.current?.editTreeInstance) {
        f3ChartRef.current.editTreeInstance.destroy();
      }
      setAdminModeActive(false);
      // Rebuild the tree to clear edit UI
      if (f3ChartRef.current) {
        // Editing can re-root the chart on a clicked card; put the founder back
        // so the rebuilt tree is the whole family again.
        f3ChartRef.current.updateMainId(TREE_ROOT_ID);
        f3ChartRef.current.updateTree({ tree_position: "inherit" });
      }
      return;
    }

    // Turning on — show a warning first
    const confirmed = window.confirm(
      "You are entering Admin Mode.\n\n" +
        "Changes you make will directly affect the family tree for ALL users. " +
        "Please be careful when adding or removing people.\n\n" +
        "Proceed?"
    );

    if (!confirmed) return;

    // Initialize the edit tree
    if (f3ChartRef.current && f3CardRef.current) {
      const f3EditTree = f3ChartRef.current.editTree();
      f3EditTree
        .setFields([
          { type: "text", label: "First Name", id: "first_name" },
          { type: "text", label: "Last Name (maiden / birth surname)", id: "last_name" },
          { type: "text", label: "Married name (optional, e.g. du Pont)", id: "married_name" },
          { type: "text", label: "Suffix", id: "numeric_suffix" },
          { type: "text", label: "Birthday", id: "birthday" },
          { type: "text", label: "Death", id: "death" },
          {
            type: "select",
            label: "Sex",
            id: "sex",
            options: [
              { value: "M", label: "Male" },
              { value: "F", label: "Female" },
            ],
          },
        ])
        .setCardClickOpen(f3CardRef.current)
        .setAddRelLabels({
          father: "Add Father",
          mother: "Add Mother",
          spouse: "Add Spouse",
          son: "Add Son",
          daughter: "Add Daughter",
        })
        .setOnSubmit(async (datum) => {
          const name = `${datum.data.first_name || ""} ${
            datum.data.last_name || ""
          }`.trim();
          // keep the card lines in sync with any married-name edit
          Object.assign(datum.data, nameLines(datum.data));

          if (datum.to_add) {
            const confirmed = window.confirm(
              `Are you sure you want to add "${name}" to the family tree?\n\n` +
                "This will be visible to all users."
            );
            if (!confirmed) return false;

            try {
              await adminCreateIndividual({
                individual_id: individualId,
                forenames: datum.data.first_name || "",
                surname: datum.data.last_name || "",
                married_name: datum.data.married_name || "",
                numeric_suffix: datum.data.numeric_suffix || "",
                sex: datum.data.sex || "",
              }).unwrap();
            } catch (err) {
              console.error("Failed to create individual:", err);
              window.alert("Failed to add person. Please try again.");
            }
          } else {
            try {
              await adminUpdateIndividual({
                id: parseInt(datum.id),
                individual_id: individualId,
                forenames: datum.data.first_name || "",
                surname: datum.data.last_name || "",
                married_name: datum.data.married_name || "",
                numeric_suffix: datum.data.numeric_suffix || "",
                sex: datum.data.sex || "",
              }).unwrap();
            } catch (err) {
              console.error("Failed to update individual:", err);
              window.alert("Failed to save changes. Please try again.");
            }
          }
        })
        .setOnDelete(async (datum) => {
          const name = `${datum.data.first_name || ""} ${
            datum.data.last_name || ""
          }`.trim();

          const confirmed = window.confirm(
            `Are you sure you want to DELETE "${name}" from the family tree?\n\n` +
              "This will remove them permanently and affect all users.\n" +
              "This action CANNOT be undone."
          );
          if (!confirmed) return false;

          try {
            await adminDeleteIndividual({
              id: parseInt(datum.id),
              individual_id: individualId,
            }).unwrap();
          } catch (err) {
            console.error("Failed to delete individual:", err);
            window.alert("Failed to delete person. Please try again.");
          }
        });

      setAdminModeActive(true);
    }
  }, [
    adminModeActive,
    individualId,
    adminCreateIndividual,
    adminUpdateIndividual,
    adminDeleteIndividual,
  ]);

  // Navigate within lineage view — click a person to see THEIR ancestry
  const handleLineageNavigate = useCallback(
    (personId, breadcrumbIndex) => {
      if (breadcrumbIndex !== undefined) {
        // Clicked a breadcrumb — go back to that point in history
        setLineageHistory((prev) => {
          const sliced = prev.slice(0, breadcrumbIndex + 1);
          setLineageTarget(sliced[sliced.length - 1]?.id);
          return sliced;
        });
        return;
      }
      // Normal navigation — add to history
      const personData = familyTree?.find((n) => String(n.id) === String(personId));
      const name = personData
        ? `${personData.data.first_name || ""} ${personData.data.last_name || ""} ${personData.data.numeric_suffix || ""}`.trim()
        : "Unknown";
      setLineageHistory((prev) => [...prev, { id: personId, name }]);
      setLineageTarget(personId);
    },
    [familyTree]
  );

  // Close lineage view and return to main tree
  const handleLineageClose = useCallback(() => {
    setLineageTarget(null);
    setLineageHistory([]);
  }, []);

  const renderCurrentUser = () => {
    if (!familyTree) return null;
    const targetNode = familyTree.find((node) => node.id === user.externalId);
    return targetNode ? formatName(targetNode.data) : null;
  };

  if (familyTreeError) {
    const isAuthError = familyTreeError?.status === 401;
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-y-3">
        <p className="font-display text-lg" style={{ color: "#b5a03e" }}>
          {isAuthError ? "Session expired" : "Unable to load family tree"}
        </p>
        <p className="text-dupontGray text-sm">
          {isAuthError
            ? "Please log out and log back in to refresh your session."
            : "Please refresh the page. If this persists, the server may be restarting."}
        </p>
        {!isAuthError && (
          <button
            type="button"
            onClick={refetchFamilyTree}
            disabled={isFetchingFamilyTree}
            className="mt-[6px] px-6 py-2.5 bg-dupontBlue text-white rounded-full text-[15px] font-medium hover:bg-dupontBlue/90 transition-colors disabled:opacity-50"
          >
            {isFetchingFamilyTree ? "Trying again…" : "Try again"}
          </button>
        )}
      </div>
    );
  }

  if (!familyTree && !isLoadingFamilyTree) {
    return <div>No family tree found</div>;
  }

  return (
    <div className="flex flex-col w-full gap-y-[10px] grow relative">
      {showCurtain && (
        <FamilyTreeLoading
          isLoading={isLoadingFamilyTree || !chartReady}
          onRevealComplete={() => setShowCurtain(false)}
        />
      )}

      {/* Top bar: current user, search, reset, admin toggle */}
      <div className="flex flex-row items-end justify-between flex-wrap gap-[8px]">
        <div className="flex flex-row items-center gap-x-[10px]" style={{ flex: 1 }}>
          <div className="text-dupontGray">Current User:</div>
          <div>{renderCurrentUser()}</div>
        </div>

        {/* Search bar — separate from the tree */}
        <div className="relative" style={{ minWidth: 280, maxWidth: 420, flex: 1 }}>
          <input
            type="text"
            placeholder="Search for a family member..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => {
              if (searchedFor) setShowSearchDropdown(true);
            }}
            onBlur={() => {
              // Small delay so click on dropdown item registers first
              setTimeout(() => setShowSearchDropdown(false), 200);
            }}
            className="w-full px-[16px] py-[10px] rounded-full text-[16px] font-body"
            style={{
              background: "rgba(0, 9, 28, 0.85)",
              border: "1px solid #b5a03e",
              color: "#fff",
              outline: "none",
            }}
          />
          {showSearchDropdown && searchedFor && searchResults.length === 0 && (
            <div
              className="absolute left-0 right-0 mt-[4px] rounded-[8px] px-[12px] py-[8px] text-[14px] font-body"
              role="status"
              style={{
                background: "rgba(0, 9, 28, 0.98)",
                border: "1px solid #b5a03e",
                color: "rgba(255,255,255,0.75)",
                zIndex: 50,
              }}
            >
              No matches for “{searchedFor}”
            </div>
          )}
          {showSearchDropdown && searchResults.length > 0 && (
            <div
              className="absolute left-0 right-0 mt-[4px] rounded-[8px] overflow-hidden"
              style={{
                background: "rgba(0, 9, 28, 0.98)",
                border: "1px solid #b5a03e",
                zIndex: 50,
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              {searchResults.map((person) => (
                <button
                  key={person.id}
                  type="button"
                  className="w-full text-left px-[12px] py-[8px] text-[14px] font-body transition-colors"
                  style={{ color: "#fff" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "rgba(181, 160, 62, 0.2)";
                    e.currentTarget.style.color = "#d4c26a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSearchSelect(person);
                  }}
                >
                  {formatName(person.data)}{" "}
                  {person.data.birthday && (
                    <span style={{ color: "rgba(255,255,255,0.5)", marginLeft: 8 }}>
                      {person.data.birthday}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-x-[10px]" style={{ flex: 1 }}>
          {/* Legend — inline here beside Reset Tree, rather than floating over the
              canvas where it covered person cards. */}
          {!lineageTarget && <TreeLegend />}

          {/* Admin mode toggle — only visible to admins.
              whitespace-nowrap: it is the only two-word label in this row, so it
              was the only button that wrapped when the row got crowded. */}
          {isAdmin && (
            <button
              type="button"
              onClick={handleAdminModeToggle}
              className="rounded-full px-[11px] py-[6px] text-[12px] font-display font-semibold transition-colors duration-200 whitespace-nowrap"
              style={{
                background: adminModeActive
                  ? "rgba(220, 50, 50, 0.2)"
                  : "rgba(181, 160, 62, 0.15)",
                border: adminModeActive
                  ? "1px solid #dc3232"
                  : "1px solid #b5a03e",
                color: adminModeActive ? "#dc3232" : "#b5a03e",
              }}
            >
              {adminModeActive ? "Exit Admin Mode" : "Admin Mode"}
            </button>
          )}

          <SharedButton
            className="inline-block rounded-full px-[11px] py-[6px] text-[12px] w-fit h-fit text-center whitespace-nowrap"
            disabled={isResettingTree}
            loadingText={isResettingTree ? "Resetting tree..." : undefined}
            onClick={() => {
              setIsResettingTree(true);
              handleResetFullTree();
            }}
          >
            Reset Tree
          </SharedButton>
        </div>
      </div>

      {/* Admin mode warning banner */}
      {adminModeActive && (
        <div
          className="flex items-center gap-x-[8px] px-[14px] py-[8px] rounded-[8px] text-[12px] font-body"
          style={{
            background: "rgba(220, 50, 50, 0.1)",
            border: "1px solid rgba(220, 50, 50, 0.3)",
            color: "#ff6b6b",
          }}
        >
          Admin Mode is active — click any card to edit. Changes affect all users.
        </div>
      )}

      {/* The family tree canvas — hidden (not destroyed) when lineage view is open */}
      <div
        className={lineageTarget ? "hidden" : "f3"}
        id="FamilyChart"
        ref={chartRef}
        style={{
          width: "100%",
          height: "100%",
          margin: "auto",
          backgroundColor: "rgb(33,33,33)",
          color: "#fff",
        }}
      />

      {/* Lineage mode — ancestry mini-tree */}
      {lineageTarget && (
        <LineageView
          tree={familyTree}
          targetId={lineageTarget}
          onClose={handleLineageClose}
          onNavigate={handleLineageNavigate}
          breadcrumbs={lineageHistory}
        />
      )}

      {/* Zoom slider — bottom-right corner, vertical (hidden during lineage view) */}
      <div
        style={{ position: "absolute", bottom: 16, right: 16, zIndex: 25, display: lineageTarget ? "none" : undefined }}
        className="flex flex-col items-center gap-y-[4px]"
      >
        <span
          className="font-display text-[11px] font-bold select-none"
          style={{ color: "#b5a03e" }}
        >
          +
        </span>
        <input
          type="range"
          min="0.1"
          max="1.3"
          step="0.02"
          defaultValue="1"
          aria-label="Zoom level"
          className="zoom-slider"
          onChange={(e) => {
            if (f3ChartRef.current) {
              f3.handlers.zoomTo(
                f3ChartRef.current.svg,
                parseFloat(e.target.value)
              );
            }
          }}
        />
        <span
          className="font-display text-[11px] font-bold select-none"
          style={{ color: "#b5a03e" }}
        >
          −
        </span>
      </div>
    </div>
  );
}

export default TreeView;
